import React, { useEffect, useState } from 'react';
import Comment from '../models/Comment';
import Helpers from '../Helpers';
import { relativeTime, senderInitials } from '../utils';

const CommentsCard = ({ track, getCurrentPosition, onSeek }) => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [text, setText] = useState('');
    const [posting, setPosting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!track) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        Comment.getCommentsForTrack(track)
            .then((c) => { if (!cancelled) setComments(c); })
            .catch(() => { if (!cancelled) setComments([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [track?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async (e) => {
        e.preventDefault();
        const trimmed = text.trim();
        if (!trimmed || posting || !track) return;
        const atSeconds = Math.floor(getCurrentPosition?.() || 0);
        setPosting(true);
        setError('');
        try {
            const id = await Comment.post(track, trimmed, atSeconds);
            const optimistic = new Comment(
                id,
                trimmed,
                atSeconds,
                Date.now(),
                '',
                'You',
                null,
            );
            setComments((prev) => [...prev, optimistic]);
            setText('');
        } catch (err) {
            setError(err?.message || 'Could not post comment.');
        } finally {
            setPosting(false);
        }
    };

    if (!track) return null;

    const headerLabel = `${comments.length} ${comments.length === 1 ? 'COMMENT' : 'COMMENTS'} ON "${(track.name || 'TRACK').toUpperCase()}"`;

    return (
        <div className="comments-card" key={track.id}>
            <div className="comments-card-header">{headerLabel}</div>
            <div className="comments-list">
                {loading && <div className="comments-empty">Loading…</div>}
                {!loading && comments.length === 0 && (
                    <div className="comments-empty">No comments yet — be the first.</div>
                )}
                {!loading && comments.map((c) => (
                    <div className="comment-row" key={c.id}>
                        <div className="comment-avatar">{senderInitials(c.senderName)}</div>
                        <div className="comment-body">
                            <div className="comment-line">
                                <span className="comment-name">{c.senderName || 'Someone'}</span>
                                <button
                                    type="button"
                                    className="comment-at"
                                    onClick={() => onSeek?.(c.atSeconds)}
                                    title="Jump to this moment"
                                >
                                    @{Helpers.formatDuration(c.atSeconds || 0)}
                                </button>
                                <span className="comment-time">{relativeTime(c.timeCreated)}</span>
                            </div>
                            <div className="comment-text">{c.text}</div>
                        </div>
                    </div>
                ))}
            </div>
            {error && <div className="comments-error">{error}</div>}
            <form className="comment-input" onSubmit={submit}>
                <input
                    type="text"
                    placeholder="Add a comment…"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    disabled={posting}
                />
                <button
                    type="submit"
                    className="btn-primary"
                    disabled={posting || !text.trim()}
                >
                    {posting ? 'Posting…' : 'Post'}
                </button>
            </form>
        </div>
    );
};

export default CommentsCard;
